import { Hydrate } from '@tanstack/react-query';
import type { AppProps as NextAppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import NProgress from 'nprogress';
import React from 'react';
import { EV_TRACK_KEY, __prod__ } from '@/lib/constants';
import { trackEvent } from '@/lib/eventTracking';
import { IsSsrMobileContext } from '@/shared-hooks/useIsMobile';
import { AppProviders } from 'AppProviders';
import nextI18nextConfig from '../../next-i18next.config.js';
import '../styles/global.css';

if (__prod__) {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  console.log = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  console.error = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  console.debug = () => {};
}

// Workaround for https://github.com/zeit/next.js/issues/8592
export type AppProps = NextAppProps & {
  /** Will be defined only is there was an error */
  err?: Error;
};

function App({ Component, pageProps, err }: AppProps) {
  const router = useRouter();

  NProgress.configure({ showSpinner: false });

  React.useEffect(() => {
    router.events.on('routeChangeStart', () => NProgress.start());
    router.events.on('routeChangeComplete', () => NProgress.done());
    router.events.on('routeChangeError', () => NProgress.done());
  }, [router.events]);

  return (
    <AppProviders>
      <Head>
        {process.env.NODE_ENV !== 'production' ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : null}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1,maximum-scale=1, user-scalable=no"
        />
      </Head>
      {/* Hydrate query cache */}
      <Hydrate state={pageProps.dehydratedState}>
        <IsSsrMobileContext.Provider value={pageProps.isSsrMobile}>
          <Component {...pageProps} err={err} />
        </IsSsrMobileContext.Provider>
      </Hydrate>
    </AppProviders>
  );
}

export default appWithTranslation(App, {
  ...nextI18nextConfig,
});
